import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import { toast } from 'react-toastify';
import { MdAccessTime, MdVideocam, MdVideocamOff } from "react-icons/md";
import * as faceapi from "face-api.js";
import { FiXCircle, FiCheckCircle, FiArrowLeft, FiAlertTriangle, FiShield, FiMonitor, FiMic, FiCamera, FiCheck } from "react-icons/fi";

const COLORS = {
  navy: '#0D1B4B',
  navyDark: '#0A1640',
  orange: '#FFA500',
  white: '#FFFFFF',
  text: '#2D3748',
  textLight: '#718096',
  border: '#E2E8F0',
  red: '#DC2626',
  green: '#10B981',
  blue: '#3B82F6',
  gray: '#64748B',
  grayLight: '#F1F5F9',
};

const AssessmentPage = () => {
  const { assessmentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [assessmentStatus, setAssessmentStatus] = useState('loading');
  const [validFrom, setValidFrom] = useState(null);
  const [validUntil, setValidUntil] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
const [modelsLoaded, setModelsLoaded] = useState(false);

  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState('validityCheck');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [assessmentCreator, setAssessmentCreator] = useState(null);

  // ✅ Camera States
  const mainVideoRef = useRef(null);
  const sidebarVideoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [cameraError, setCameraError] = useState('');

  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [violationCount, setViolationCount] = useState(0);
  const [maxViolations] = useState(3);
  const [warnings, setWarnings] = useState([]);

  const [candidateInfo, setCandidateInfo] = useState({
    fullName: '',
    email: '',
    mobile: ''
  });

  const [instructionsRead, setInstructionsRead] = useState(false);
  const countdownIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null)

  const [warningCount, setWarningCount] = useState(0);
const [faceWarning, setFaceWarning] = useState("");
const faceIntervalRef = useRef(null);

// ✅ NEW: Store all proctoring data
const [photoCapture, setPhotoCapture] = useState(null);
const [screenShareData, setScreenShareData] = useState({ active: false, startedAt: null, endedAt: null });
const [micTestData, setMicTestData] = useState({ passed: false, testedAt: null, audioLevel: 0 });
const [timeline, setTimeline] = useState([]);
const [cameraSnapshots, setCameraSnapshots] = useState([]);
const snapshotIntervalRef = useRef(null);

// ✅ Add timeline event
const addTimelineEvent = (event, data = {}) => {
  const newEvent = {
    event,
    timestamp: new Date().toISOString(),
    data
  };
  setTimeline(prev => [...prev, newEvent]);
  console.log('📝 Timeline:', event);
};

// ✅ Capture photo for identity verification
const captureIdentityPhoto = () => {
  if (!sidebarVideoRef.current || sidebarVideoRef.current.readyState !== 4) return;
  
  try {
    const canvas = document.createElement('canvas');
    canvas.width = sidebarVideoRef.current.videoWidth;
    canvas.height = sidebarVideoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(sidebarVideoRef.current, 0, 0);
    const imageData = canvas.toDataURL('image/jpeg', 0.7);
    
    setPhotoCapture({
      timestamp: new Date().toISOString(),
      imageData: imageData,
      faceDetected: true
    });
    
    addTimelineEvent('photo_captured', { timestamp: new Date().toISOString() });
    console.log('📸 Identity photo captured');
  } catch (err) {
    console.error('Photo capture error:', err);
  }
};

// ✅ Start periodic camera snapshots
const startPeriodicSnapshots = () => {
  if (snapshotIntervalRef.current) clearInterval(snapshotIntervalRef.current);
  
  snapshotIntervalRef.current = setInterval(() => {
    if (cameraEnabled && currentStep === 'assessment' && sidebarVideoRef.current) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 320;
        canvas.height = 240;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(sidebarVideoRef.current, 0, 0, 320, 240);
        const imageData = canvas.toDataURL('image/jpeg', 0.5);
        
        setCameraSnapshots(prev => [...prev, {
          timestamp: new Date().toISOString(),
          imageData: imageData,
          faceDetected: faceWarning === ""
        }]);
        
        // Keep only last 20 snapshots
        setCameraSnapshots(prev => prev.slice(-20));
      } catch (err) {
        console.error('Snapshot error:', err);
      }
    }
  }, 30000); // Every 30 seconds
};

// ✅ Test Microphone
// ✅ FIXED: Test Microphone - No AudioContext close error
const testMicrophone = async () => {
  try {
    addTimelineEvent('mic_test_started', {});
    
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = audioContext.createAnalyser();
    const microphone = audioContext.createMediaStreamSource(stream);
    microphone.connect(analyser);
    analyser.fftSize = 256;
    
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    let maxLevel = 0;
    let isClosed = false; // ✅ Track if already closed
    
    const checkSound = () => {
      if (isClosed) return; // ✅ Don't proceed if closed
      
      try {
        analyser.getByteFrequencyData(dataArray);
        const level = Math.max(...dataArray);
        if (level > maxLevel) maxLevel = level;
        
        if (level > 30) {
          setMicTestData({
            passed: true,
            testedAt: new Date().toISOString(),
            audioLevel: maxLevel
          });
          addTimelineEvent('mic_test_passed', { audioLevel: maxLevel });
          
          // ✅ Clean up properly
          isClosed = true;
          stream.getTracks().forEach(t => t.stop());
          audioContext.close().catch(() => {});
          toast.success('✅ Microphone working!');
          return;
        }
        requestAnimationFrame(checkSound);
      } catch (err) {
        console.log('Mic test check error:', err);
      }
    };
    
    checkSound();
    
    // ✅ Timeout handler - only close if not already closed
    const timeoutId = setTimeout(() => {
      if (!micTestData.passed && !isClosed) {
        isClosed = true;
        setMicTestData({
          passed: false,
          testedAt: new Date().toISOString(),
          audioLevel: maxLevel
        });
        addTimelineEvent('mic_test_failed', { maxLevel });
        stream.getTracks().forEach(t => t.stop());
        audioContext.close().catch(() => {});
        toast.warning('Microphone test failed. Please check your mic.');
      }
    }, 5000);
    
    // ✅ Clear timeout if component unmounts
    return () => clearTimeout(timeoutId);
    
  } catch (error) {
    console.error('Mic test error:', error);
    addTimelineEvent('mic_test_error', { error: error.message });
    toast.error('Could not access microphone');
  }
};

// ✅ Start Screen Share
const startScreenShare = async () => {
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
    setScreenShareData({
      active: true,
      startedAt: new Date().toISOString(),
      endedAt: null
    });
    addTimelineEvent('screen_share_started', {});
    
    stream.getVideoTracks()[0].onended = () => {
      setScreenShareData(prev => ({
        ...prev,
        active: false,
        endedAt: new Date().toISOString()
      }));
      addTimelineEvent('screen_share_ended', {});
    };
    
    toast.success('Screen sharing started');
  } catch (err) {
    console.error('Screen share error:', err);
    addTimelineEvent('screen_share_error', { error: err.message });
  }
};

useEffect(() => {
  loadModels();
}, []);

useEffect(() => {
  if (modelsLoaded && cameraEnabled && currentStep === 'assessment') {
    console.log("🚀 Starting face detection...");
    startFaceCheck();
    startPeriodicSnapshots();
    captureIdentityPhoto(); // Capture photo once at start
  }
}, [modelsLoaded, cameraEnabled, currentStep]);

useEffect(() => {
  return () => {
    if (faceIntervalRef.current) clearInterval(faceIntervalRef.current);
    if (snapshotIntervalRef.current) clearInterval(snapshotIntervalRef.current);
  };
}, []);

  useEffect(() => {
  if (currentStep === 'assessment' && streamRef.current) {
    console.log('🔁 Re-attaching stream to sidebar video');

    if (sidebarVideoRef.current) {
      sidebarVideoRef.current.srcObject = streamRef.current;

      sidebarVideoRef.current.onloadeddata = () => {
        sidebarVideoRef.current.play().catch(() => {});
      };
    }
  }
}, [currentStep]);

  useEffect(() => {
    checkAssessmentValidity();
    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      cleanupAntiCheatingMeasures();
      stopCamera();
    };
  }, []);

const startCamera = async () => {
  try {
    setCameraError('');
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    console.log('🎥 Requesting camera access...');
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
      audio: true
    });
    console.log('✅ Camera access granted');
    streamRef.current = stream;
    
    const attachStream = (videoRef, name) => {
      if (!videoRef.current) return;
      const video = videoRef.current;
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      video.onloadeddata = () => {
        video.play().then(() => console.log(`✅ ${name} video playing`)).catch(err => console.log(`❌ ${name} play error`, err));
      };
    };
    attachStream(mainVideoRef, 'Main');
    attachStream(sidebarVideoRef, 'Sidebar');
    setTimeout(() => {
      if (mainVideoRef.current && mainVideoRef.current.paused) mainVideoRef.current.play().catch(() => {});
      if (sidebarVideoRef.current && sidebarVideoRef.current.paused) sidebarVideoRef.current.play().catch(() => {});
    }, 1000);
    setCameraEnabled(true);
    addTimelineEvent('camera_enabled', {});
    
    // Test microphone automatically
    setTimeout(() => testMicrophone(), 2000);
    
  } catch (error) {
    console.error('❌ Camera error:', error);
    let errorMsg = 'Camera access required. ';
    if (error.name === 'NotAllowedError') errorMsg += 'Please allow camera permissions.';
    else if (error.name === 'NotFoundError') errorMsg += 'No camera found.';
    else if (error.name === 'NotReadableError') errorMsg += 'Camera is already in use.';
    else errorMsg += error.message;
    setCameraError(errorMsg);
    toast.error(errorMsg);
    setCameraEnabled(false);
  }
};

const loadModels = async () => {
  const MODEL_URL = "/models";
  await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
  await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
  console.log("✅ Face models loaded");
  setModelsLoaded(true);
};

const startFaceCheck = () => {
  if (faceIntervalRef.current) return;
  faceIntervalRef.current = setInterval(async () => {
    const video = sidebarVideoRef.current;
    if (!video || video.readyState !== 4) return;
    try {
      const detection = await faceapi.detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ inputSize: 320 })).withFaceLandmarks();
      if (!detection) {
        triggerWarning("Face not detected ❌", "no_face");
        return;
      }
      const landmarks = detection.landmarks;
      const nose = landmarks.getNose()[3];
      const leftEye = landmarks.getLeftEye()[0];
      const rightEye = landmarks.getRightEye()[3];
      const faceCenter = (leftEye.x + rightEye.x) / 2;
      const faceWidth = Math.abs(rightEye.x - leftEye.x);
      const threshold = faceWidth * 0.08;
      if (nose.x < faceCenter - threshold) {
        triggerWarning("Looking LEFT ❌", "looking_away");
      } else if (nose.x > faceCenter + threshold) {
        triggerWarning("Looking RIGHT ❌", "looking_away");
      } else {
        setTimeout(() => setFaceWarning(""), 2000);
      }
    } catch (err) {
      console.log("Face detection error:", err);
    }
  }, 2000);
};

const triggerWarning = (msg, type) => {
  if (faceWarning === msg) return;
  console.log("⚠️ WARNING:", msg);
  setFaceWarning("");
  setTimeout(() => setFaceWarning(msg), 100);
  setWarningCount(prev => prev + 1);
  setViolationCount(prev => prev + 1);
  toast.error(msg);
  addTimelineEvent('face_violation', { message: msg, type: type });
  setWarnings(prev => [{ id: Date.now(), message: msg, time: new Date().toLocaleTimeString(), type: 'face' }, ...prev.slice(0, 4)]);
  if (warningCount + 1 >= 3) {
    toast.error('Multiple warnings! Auto-submitting...');
    setTimeout(() => handleSubmit(), 2000);
  }
};

  const stopCamera = () => {
    console.log('🛑 Stopping camera...');
    if (faceIntervalRef.current) clearInterval(faceIntervalRef.current);
    if (snapshotIntervalRef.current) clearInterval(snapshotIntervalRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (mainVideoRef.current) mainVideoRef.current.srcObject = null;
    if (sidebarVideoRef.current) sidebarVideoRef.current.srcObject = null;
    setCameraEnabled(false);
  };

  const checkAssessmentValidity = async () => {
    try {
      setLoading(true);
      const candidateEmail = searchParams.get('candidateEmail');
      if (!candidateEmail) {
        setAssessmentStatus('invalid_access');
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API_BASE_URL}/api/assessments/${assessmentId}/validity`, {
          params: { candidateEmail, applicationId: searchParams.get('applicationId') }
        });
        if (response.data.success && response.data.data.status === 'valid') {
          setAssessmentStatus('valid');
          await loadAssessmentData();
          setCurrentStep('instructions');
        }
      } catch (error) {
        if (error.response?.status === 403) {
          const data = error.response.data.data;
          setAssessmentStatus('not_started');
          setValidFrom(new Date(data.validFrom));
          setValidUntil(new Date(data.validUntil));
          const timeRemaining = new Date(data.validFrom) - new Date();
          if (timeRemaining > 0) {
            updateCountdown(timeRemaining);
            startCountdownTimer(timeRemaining);
          }
        } else if (error.response?.status === 410) {
          setAssessmentStatus('expired');
        } else if (error.response?.status === 409) {
          setAssessmentStatus('already_submitted');
        } else {
          setAssessmentStatus('error');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setAssessmentStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const startCountdownTimer = (initialTime) => {
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    countdownIntervalRef.current = setInterval(() => {
      const timeRemaining = new Date(validFrom) - new Date();
      if (timeRemaining <= 0) {
        clearInterval(countdownIntervalRef.current);
        window.location.reload();
        return;
      }
      updateCountdown(timeRemaining);
    }, 1000);
  };

  const updateCountdown = (timeRemaining) => {
    setCountdown({
      days: Math.floor(timeRemaining / (1000 * 60 * 60 * 24)),
      hours: Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((timeRemaining % (1000 * 60)) / 1000)
    });
  };

  const loadAssessmentData = async () => {
    try {
      const candidateEmail = searchParams.get('candidateEmail');
      const response = await axios.get(`${API_BASE_URL}/api/assessments/${assessmentId}`, {
        params: {
          candidateEmail,
          candidateName: searchParams.get('candidateName'),
          candidateMobile: searchParams.get('candidateMobile'),
          applicationId: searchParams.get('applicationId')
        }
      });
      if (response.data.success) {
        const assessmentData = response.data.data;
        setAssessment(assessmentData.assessment);
        setAssessmentCreator(assessmentData.assessment?.createdBy);
        setQuestions(assessmentData.questions || []);
        setTimeLeft((assessmentData.assessment?.duration || 60) * 60);
        if (candidateEmail) {
          setCandidateInfo({
            fullName: searchParams.get('candidateName') || '',
            email: candidateEmail,
            mobile: searchParams.get('candidateMobile') || ''
          });
        }
        console.log('📦 Loaded questions:', assessmentData.questions?.length);
      }
    } catch (error) {
      console.error('Error loading assessment:', error);
      setAssessmentStatus('error');
    }
  };

  const setupAntiCheatingMeasures = () => {
    document.addEventListener('contextmenu', (e) => e.preventDefault());
    document.addEventListener('copy', (e) => e.preventDefault());
    document.addEventListener('cut', (e) => e.preventDefault());
    document.addEventListener('paste', (e) => e.preventDefault());
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) handleWindowBlur();
    });
    window.addEventListener('blur', handleWindowBlur);
  };

  const cleanupAntiCheatingMeasures = () => {
    document.removeEventListener('contextmenu', (e) => e.preventDefault());
    document.removeEventListener('copy', (e) => e.preventDefault());
    document.removeEventListener('cut', (e) => e.preventDefault());
    document.removeEventListener('paste', (e) => e.preventDefault());
    document.removeEventListener('keydown', handleKeyDown);
    document.removeEventListener('visibilitychange', () => {});
    window.removeEventListener('blur', handleWindowBlur);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && ['I', 'C'].includes(e.key)) || (e.ctrlKey && ['u', 'c', 'v'].includes(e.key))) {
      e.preventDefault();
      addWarning('Shortcuts disabled');
      addTimelineEvent('devtools_blocked', { key: e.key });
    }
  };

  const handleWindowBlur = () => {
    if (currentStep === 'assessment' && !isSubmitted) {
      const newCount = tabSwitchCount + 1;
      setTabSwitchCount(newCount);
      addWarning(`Tab switch (${newCount}/${maxViolations})`);
      addTimelineEvent('tab_switch', { count: newCount });
      if (newCount >= maxViolations) {
        toast.error('Auto-submitting due to violations');
        handleSubmit();
      }
    }
  };

  const addWarning = (message) => {
    setWarnings(prev => [{ id: Date.now(), message, time: new Date().toLocaleTimeString(), type: 'tab' }, ...prev.slice(0, 4)]);
    setViolationCount(prev => prev + 1);
    addTimelineEvent('violation', { message });
  };

  const startTimer = () => {
    timerIntervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerIntervalRef.current);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleAutoSubmit = () => {
    if (!isSubmitted && !submitting) {
      toast.info('Time is up! Submitting...');
      handleSubmit();
    }
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleStartAssessment = () => {
    if (!instructionsRead) {
      toast.error('Please accept the instructions to continue');
      return;
    }
    setCurrentStep('cameraSetup');
  };

  const handleCameraComplete = async () => {
    if (!cameraEnabled) {
      toast.error('Please enable camera to continue');
      return;
    }
    // Ask for screen share
    const shareScreen = window.confirm('Share your screen for proctoring?');
    if (shareScreen) {
      await startScreenShare();
    }
    setCurrentStep('assessment');
    startTimer();
    setupAntiCheatingMeasures();
    addTimelineEvent('assessment_started', { timeLeft: timeLeft });
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
    addTimelineEvent('answer_changed', { questionId });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) setCurrentQuestion(prev => prev + 1);
    addTimelineEvent('question_navigated', { to: currentQuestion + 1 });
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) setCurrentQuestion(prev => prev - 1);
    addTimelineEvent('question_navigated', { to: currentQuestion - 1 });
  };

  // ✅ FIXED: Complete handleSubmit with ALL proctoring data
  const handleSubmit = async () => {
    if (submitting) return;
    if (!window.confirm('Submit assessment? You cannot change answers after submission.')) return;

    try {
      setSubmitting(true);
      addTimelineEvent('submission_started', {});
      
      // ✅ Complete proctoring data
      const proctoringData = {
        faceWarnings: warningCount,
        faceWarningMessages: warnings.filter(w => w.type === 'face'),
        tabSwitches: tabSwitchCount,
        totalViolations: violationCount,
        allWarnings: warnings,
        cameraSnapshots: cameraSnapshots,
        screenShare: screenShareData,
        micTest: micTestData,
        timeline: timeline,
        photoCapture: photoCapture,
        submittedAt: new Date().toISOString()
      };
      
      const response = await axios.post(`${API_BASE_URL}/api/assessments/${assessmentId}/submit`, {
        assessmentId,
        candidateEmail: candidateInfo.email,
        candidateName: candidateInfo.fullName,
        candidateMobile: candidateInfo.mobile,
        candidateInfo: candidateInfo,
        answers: answers,
        timeSpent: (assessment?.duration || 60) * 60 - timeLeft,
        violations: { 
          tabSwitches: tabSwitchCount, 
          faceDetectionIssues: warningCount,
          copyPasteAttempts: 0,
          totalViolations: violationCount 
        },
        proctoringData: proctoringData,
        photoCapture: photoCapture,
        screenShare: screenShareData,
        micTest: micTestData,
        faceWarnings: warnings.filter(w => w.type === 'face'),
        timeline: timeline,
        applicationId: searchParams.get('applicationId')
      });

      if (response.data.success) {
        stopCamera();
        setSubmissionResult(response.data.data);
        setIsSubmitted(true);
        cleanupAntiCheatingMeasures();
        addTimelineEvent('submission_completed', { submissionId: response.data.data.submissionId });
        toast.success('Assessment submitted successfully!');
      }
    } catch (error) {
      console.error('Submission error:', error);
      addTimelineEvent('submission_failed', { error: error.message });
      toast.error(error.response?.data?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ==================== RENDER FUNCTIONS (rest remains same) ====================
  
  const renderValidityCheck = () => {
    if (loading) {
      return (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Checking assessment availability...</p>
        </div>
      );
    }
    if (assessmentStatus === 'not_started') {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.iconWrapper(COLORS.orange)}>⏰</div>
            <h2 style={styles.title(COLORS.orange)}>Assessment Not Started</h2>
            <p style={styles.subtitle}>Available at: {validFrom?.toLocaleString()}</p>
            <div style={styles.countdownBox}>
              <div style={styles.countdownUnit}><span style={styles.countdownValue}>{String(countdown.days).padStart(2, '0')}</span><span>Days</span></div>
              <div style={styles.countdownUnit}><span style={styles.countdownValue}>{String(countdown.hours).padStart(2, '0')}</span><span>Hours</span></div>
              <div style={styles.countdownUnit}><span style={styles.countdownValue}>{String(countdown.minutes).padStart(2, '0')}</span><span>Minutes</span></div>
              <div style={styles.countdownUnit}><span style={styles.countdownValue}>{String(countdown.seconds).padStart(2, '0')}</span><span>Seconds</span></div>
            </div>
            <button onClick={() => navigate('/')} style={styles.buttonSecondary}>Back to Home</button>
          </div>
        </div>
      );
    }
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.iconWrapper(COLORS.red)}>⚠️</div>
          <h2 style={styles.title(COLORS.red)}>Invalid Access</h2>
          <button onClick={() => navigate('/')} style={styles.buttonSecondary}>Back to Home</button>
        </div>
      </div>
    );
  };

  const renderInstructions = () => (
    <div style={styles.container}>
      <div style={styles.cardLarge}>
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>{assessment?.title || 'Online Assessment'}</h1>
          <div style={styles.headerBadge}>{questions.length} Questions</div>
        </div>
        <div style={styles.content}>
          <h3 style={styles.sectionTitle}>Assessment Instructions</h3>
          <div style={styles.instructionGrid}>
            <div style={styles.instructionCard(COLORS.blue)}><FiShield size={24} color={COLORS.blue} /><h4>Proctoring Active</h4><p>Camera monitoring • Face detection</p></div>
            <div style={styles.instructionCard(COLORS.green)}><FiMonitor size={24} color={COLORS.green} /><h4>Duration</h4><p>{assessment?.duration || 60} minutes</p></div>
            <div style={styles.instructionCard(COLORS.orange)}><FiCamera size={24} color={COLORS.orange} /><h4>Camera Required</h4><p>Keep face visible throughout</p></div>
            <div style={styles.instructionCard(COLORS.red)}><FiAlertTriangle size={24} color={COLORS.red} /><h4>Anti-Cheating</h4><p>No tab switching • No copy-paste</p></div>
          </div>
          <div style={styles.rulesBox}>
            <h4 style={{ marginBottom: 12, color: COLORS.navy }}>⚠️ Important Rules</h4>
            <ul style={styles.rulesList}>
              <li><FiXCircle color={COLORS.red} size={14} /> Do not switch tabs or open new windows</li>
              <li><FiXCircle color={COLORS.red} size={14} /> Copy-paste and right-click are disabled</li>
              <li><FiCheckCircle color={COLORS.green} size={14} /> Keep camera on and face visible</li>
              <li><FiCheckCircle color={COLORS.green} size={14} /> Complete all questions before timer ends</li>
            </ul>
          </div>
          <label style={styles.agreement}>
            <input type="checkbox" checked={instructionsRead} onChange={(e) => setInstructionsRead(e.target.checked)} />
            <span>I have read and understood all instructions. I agree to follow the rules.</span>
          </label>
          <button onClick={handleStartAssessment} disabled={!instructionsRead} style={instructionsRead ? styles.buttonPrimary : styles.buttonDisabled}>
            Start Assessment →
          </button>
        </div>
      </div>
    </div>
  );

  const renderCameraSetup = () => (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Camera Setup</h2>
        <p style={styles.subtitle}>Please enable your camera for proctoring</p>
        <div style={styles.cameraBox}>
          <video ref={mainVideoRef} autoPlay playsInline muted style={styles.cameraVideo} />
          {!cameraEnabled && !cameraError && (
            <div style={styles.cameraOverlay}>
              <FiCamera size={48} color={COLORS.textLight} />
              <p>Camera feed will appear here</p>
              <button onClick={startCamera} style={styles.enableCameraBtn}>Enable Camera</button>
            </div>
          )}
          {cameraError && (
            <div style={styles.cameraErrorOverlay}>
              <p style={{ color: COLORS.red }}>❌ {cameraError}</p>
              <button onClick={startCamera} style={styles.enableCameraBtn}>Try Again</button>
            </div>
          )}
          {cameraEnabled && <div style={styles.cameraStatusBadge}><span>● Recording</span></div>}
        </div>
        <div style={styles.buttonGroup}>
          <button onClick={handleCameraComplete} disabled={!cameraEnabled} style={!cameraEnabled ? styles.buttonDisabled : styles.buttonPrimary}>
            Continue to Assessment →
          </button>
        </div>
      </div>
    </div>
  );

  const renderAssessment = () => {
    if (questions.length === 0) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <p>No questions available</p>
            <button onClick={() => window.close()}>Close</button>
          </div>
        </div>
      );
    }
    const q = questions[currentQuestion];
    const answeredCount = Object.keys(answers).length;
    const progressPercent = (answeredCount / questions.length) * 100;
    return (
      <div style={styles.assessmentLayout}>
        <div style={styles.assessmentHeader}>
          <div><h2 style={{ color: COLORS.white, margin: 0 }}>{assessment?.title}</h2><p style={{ color: COLORS.textLight, fontSize: 12, margin: '5px 0 0' }}>{candidateInfo.fullName} • {questions.length} questions</p></div>
          <div style={styles.timerBox}><MdAccessTime size={18} color={COLORS.orange} /><span style={styles.timerValue}>{formatTime(timeLeft)}</span></div>
        </div>
        {faceWarning !== "" && <div style={styles.warningBanner}><FiAlertTriangle size={18} color={COLORS.red} /><span style={{ color: COLORS.red, fontWeight: 600 }}>{faceWarning}</span><span style={{ marginLeft: 'auto', fontSize: 12 }}>Warning {warningCount}/3</span></div>}
        <div style={styles.assessmentMain}>
          <div style={styles.questionNav}>{questions.map((_, idx) => (<button key={idx} onClick={() => setCurrentQuestion(idx)} style={styles.navDot(currentQuestion === idx, !!answers[questions[idx]._id])}>{idx + 1}</button>))}</div>
          <div style={styles.progressBar}><div style={styles.progressFill(progressPercent)} /></div>
          <div style={styles.questionCard}>
            <div style={styles.questionHeader}><span>Question {currentQuestion + 1} of {questions.length}</span><span style={styles.questionType}>{q.type?.replace('_', ' ')}</span></div>
            <div style={styles.questionBody}>
              <p style={styles.questionText}>{q.question}</p>
              {q.type === 'multiple_choice' && q.options && (<div style={styles.options}>{q.options.map((opt, idx) => (<div key={idx} onClick={() => handleAnswerChange(q._id, opt)} style={styles.option(answers[q._id] === opt)}><div style={styles.radio(answers[q._id] === opt)} /><span>{opt}</span></div>))}</div>)}
              {['text', 'scenario', 'case_study'].includes(q.type) && (<textarea value={answers[q._id] || ''} onChange={(e) => handleAnswerChange(q._id, e.target.value)} placeholder="Type your answer here..." style={styles.textarea} rows={6} />)}
              <div style={styles.questionNavButtons}>
                <button onClick={handlePrevious} disabled={currentQuestion === 0} style={styles.navButtonSecondary}>← Previous</button>
                {currentQuestion === questions.length - 1 ? (<button onClick={handleSubmit} disabled={submitting} style={styles.submitButton}>{submitting ? 'Submitting...' : 'Submit Assessment'}</button>) : (<button onClick={handleNext} style={styles.navButtonPrimary}>Next →</button>)}
              </div>
            </div>
          </div>
        </div>
        <div style={styles.sidebar}>
          <div style={styles.cameraSidebar}>
            <h4 style={{ margin: '0 0 10px 0', color: COLORS.navy }}>Live Feed</h4>
            <div style={styles.cameraPreviewBox}><video ref={sidebarVideoRef} autoPlay playsInline muted style={styles.cameraPreviewVideo} />{!cameraEnabled && (<div style={styles.cameraPreviewOverlay}><FiCamera size={24} color={COLORS.textLight} /><p style={{ fontSize: 11 }}>Camera off</p></div>)}</div>
            <div style={styles.statusBadge(cameraEnabled)}>{cameraEnabled ? '● Recording' : '○ Camera Off'}</div>
            {faceWarning !== "" && (<div style={styles.faceWarningBadge}>⚠ {faceWarning}</div>)}
            {warningCount > 0 && (<div style={styles.warningCountBadge}>Warnings: {warningCount} / 3</div>)}
          </div>
          <div style={styles.progressSidebar}>
            <h4 style={{ margin: 0 }}>Progress</h4>
            <div style={styles.progressStats}><span>{answeredCount}/{questions.length} Answered</span><span>{Math.round(progressPercent)}%</span></div>
            <div style={styles.progressBarSmall}><div style={styles.progressFillSmall(progressPercent)} /></div>
          </div>
          {violationCount > 0 && (<div style={styles.warningSidebar}><h4 style={{ margin: 0 }}>⚠️ Violations</h4><div style={styles.warningCount}>{violationCount}/{maxViolations}</div>{warnings.slice(0, 3).map(w => (<div key={w.id} style={styles.warningItem}>{w.message}</div>))}</div>)}
        </div>
      </div>
    );
  };

  const renderSubmitted = () => (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.iconWrapper(COLORS.green)}>✅</div>
        <h2 style={styles.title(COLORS.green)}>Assessment Submitted!</h2>
        <p>Thank you for completing the assessment.</p>
        {submissionResult && (<div style={styles.resultBox}><p><strong>Submission ID:</strong> {submissionResult.submissionId}</p></div>)}
        <button onClick={() => window.close()} style={styles.buttonPrimary}>Close Window</button>
      </div>
    </div>
  );

  if (currentStep === 'validityCheck') return renderValidityCheck();
  if (isSubmitted) return renderSubmitted();
  if (currentStep === 'instructions') return renderInstructions();
  if (currentStep === 'cameraSetup') return renderCameraSetup();
  if (currentStep === 'assessment') return renderAssessment();

  return null;
};

// ==================== STYLES (same as before - keeping existing) ====================
const styles = {
  container: { minHeight: '100vh', background: `linear-gradient(135deg, ${COLORS.navy} 0%, ${COLORS.navyDark} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: "'Inter', -apple-system, sans-serif" },
  loadingContainer: { minHeight: '100vh', background: `linear-gradient(135deg, ${COLORS.navy} 0%, ${COLORS.navyDark} 100%)`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' },
  spinner: { width: '50px', height: '50px', border: `3px solid rgba(255,255,255,0.2)`, borderTop: `3px solid ${COLORS.orange}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingText: { color: COLORS.white, fontSize: '14px' },
  card: { maxWidth: '500px', width: '100%', background: COLORS.white, borderRadius: '20px', padding: '40px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' },
  cardLarge: { maxWidth: '800px', width: '100%', background: COLORS.white, borderRadius: '20px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' },
  header: { background: COLORS.navy, padding: '30px', textAlign: 'center' },
  headerTitle: { color: COLORS.white, fontSize: '24px', marginBottom: '10px' },
  headerBadge: { display: 'inline-block', background: COLORS.orange, color: COLORS.white, padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  content: { padding: '30px' },
  sectionTitle: { fontSize: '20px', fontWeight: '700', color: COLORS.navy, marginBottom: '20px' },
  instructionGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' },
  instructionCard: (color) => ({ padding: '16px', background: `${color}10`, borderRadius: '12px', border: `1px solid ${color}20`, textAlign: 'center' }),
  rulesBox: { background: COLORS.redLight, padding: '20px', borderRadius: '12px', marginBottom: '24px' },
  rulesList: { listStyle: 'none', padding: 0, margin: 0 },
  agreement: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', padding: '16px', background: COLORS.grayLight, borderRadius: '12px', cursor: 'pointer' },
  buttonPrimary: { width: '100%', padding: '14px', background: COLORS.orange, color: COLORS.white, border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' },
  buttonSecondary: { padding: '12px 24px', background: 'transparent', color: COLORS.white, border: `2px solid ${COLORS.white}`, borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  buttonDisabled: { width: '100%', padding: '14px', background: COLORS.gray, color: COLORS.white, border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: '600', cursor: 'not-allowed' },
  iconWrapper: (color) => ({ width: '80px', height: '80px', background: `${color}15`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px', margin: '0 auto 20px' }),
  title: (color) => ({ color: color, fontSize: '24px', marginBottom: '10px' }),
  subtitle: { color: COLORS.textLight, marginBottom: '24px' },
  countdownBox: { display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '30px' },
  countdownUnit: { textAlign: 'center' },
  countdownValue: { display: 'block', fontSize: '28px', fontWeight: '700', color: COLORS.orange },
  cameraBox: { position: 'relative', background: '#000', borderRadius: '12px', overflow: 'hidden', aspectRatio: '16/9', marginBottom: '20px' },
  cameraVideo: { width: '100%', height: '100%', objectFit: 'cover' },
  cameraOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', color: COLORS.white },
  cameraErrorOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' },
  cameraStatusBadge: { position: 'absolute', bottom: '10px', right: '10px', background: COLORS.green, color: COLORS.white, padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  enableCameraBtn: { padding: '8px 20px', background: COLORS.orange, color: COLORS.white, border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  buttonGroup: { display: 'flex', gap: '12px' },
  assessmentLayout: { minHeight: '100vh', background: COLORS.grayLight, display: 'flex', flexDirection: 'column' },
  assessmentHeader: { background: COLORS.navy, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 },
  timerBox: { background: COLORS.navyDark, padding: '8px 20px', borderRadius: '30px', display: 'flex', alignItems: 'center', gap: '8px' },
  timerValue: { color: COLORS.orange, fontSize: '20px', fontWeight: '700', fontFamily: 'monospace' },
  assessmentMain: { flex: 1, padding: '24px', marginRight: '300px' },
  sidebar: { position: 'fixed', right: 0, top: '73px', width: '300px', height: 'calc(100vh - 73px)', background: COLORS.white, borderLeft: `1px solid ${COLORS.border}`, padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' },
  questionNav: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' },
  navDot: (isCurrent, isAnswered) => ({ width: '36px', height: '36px', borderRadius: '10px', border: 'none', background: isCurrent ? COLORS.orange : isAnswered ? COLORS.green : COLORS.grayLight, color: isCurrent || isAnswered ? COLORS.white : COLORS.textLight, fontWeight: '600', cursor: 'pointer' }),
  progressBar: { height: '4px', background: COLORS.border, borderRadius: '2px', marginBottom: '24px', overflow: 'hidden' },
  progressFill: (percent) => ({ height: '100%', width: `${percent}%`, background: COLORS.green, borderRadius: '2px', transition: 'width 0.3s' }),
  questionCard: { background: COLORS.white, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
  questionHeader: { padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  questionType: { background: COLORS.blueLight, color: COLORS.blue, padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  questionBody: { padding: '24px' },
  questionText: { fontSize: '18px', fontWeight: '500', color: COLORS.navy, marginBottom: '24px', lineHeight: '1.5' },
  options: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' },
  option: (selected) => ({ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: selected ? COLORS.blueLight : COLORS.grayLight, border: `1px solid ${selected ? COLORS.blue : COLORS.border}`, borderRadius: '10px', cursor: 'pointer' }),
  radio: (selected) => ({ width: '18px', height: '18px', borderRadius: '50%', border: `2px solid ${selected ? COLORS.blue : COLORS.textLight}`, background: selected ? COLORS.blue : 'transparent' }),
  textarea: { width: '100%', padding: '16px', border: `1px solid ${COLORS.border}`, borderRadius: '10px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', marginBottom: '24px' },
  questionNavButtons: { display: 'flex', justifyContent: 'space-between', gap: '12px' },
  navButtonSecondary: { padding: '10px 20px', background: COLORS.grayLight, border: 'none', borderRadius: '8px', cursor: 'pointer' },
  navButtonPrimary: { padding: '10px 24px', background: COLORS.orange, color: COLORS.white, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' },
  submitButton: { padding: '10px 24px', background: COLORS.red, color: COLORS.white, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' },
  cameraSidebar: { textAlign: 'center' },
  cameraPreviewBox: { position: 'relative', background: '#000', borderRadius: '10px', overflow: 'hidden', aspectRatio: '4/3', margin: '10px 0' },
  cameraPreviewVideo: { width: '100%', height: '100%', objectFit: 'cover' },
  cameraPreviewOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', color: COLORS.white },
  faceWarningBadge: { marginTop: '8px', padding: '6px', background: COLORS.red, color: COLORS.white, borderRadius: '6px', fontSize: '12px', fontWeight: '600', textAlign: 'center' },
  warningCountBadge: { marginTop: '6px', fontSize: '11px', color: COLORS.red, textAlign: 'center', fontWeight: '600' },
  statusBadge: (active) => ({ fontSize: '12px', color: active ? COLORS.green : COLORS.red, fontWeight: '600' }),
  progressSidebar: { padding: '16px', background: COLORS.grayLight, borderRadius: '12px' },
  progressStats: { display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' },
  progressBarSmall: { height: '6px', background: COLORS.border, borderRadius: '3px', overflow: 'hidden' },
  progressFillSmall: (percent) => ({ height: '100%', width: `${percent}%`, background: COLORS.green, borderRadius: '3px' }),
  warningSidebar: { padding: '16px', background: COLORS.redLight, borderRadius: '12px' },
  warningCount: { fontSize: '24px', fontWeight: '700', color: COLORS.red, marginBottom: '8px' },
  warningItem: { fontSize: '12px', color: COLORS.red, marginBottom: '4px' },
  warningBanner: { background: COLORS.redLight, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: `1px solid ${COLORS.red}` },
  resultBox: { background: COLORS.grayLight, padding: '16px', borderRadius: '12px', margin: '20px 0', textAlign: 'left' },
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;
document.head.appendChild(styleSheet);

export default AssessmentPage;